/**
 * Checks if an object can be safely serialized to JSON without losing data
 */
const isJSONable = (obj: unknown): boolean => {
    try {
        const str = JSON.stringify(obj);
        JSON.parse(str);
        return true;
    } catch {
        return false;
    }
};

/**
 * Checks if value is a plain object (not array, null, or special object types)
 */
const isPlainObject = (value: unknown): value is object => {
    if (value === null || value === undefined) return false;
    const proto = Object.getPrototypeOf(value);
    return proto === Object.prototype || proto === null;
};

/**
 * Flattens a nested object structure into a single-level object with concatenated keys.
 *
 * @param obj - The object to flatten
 * @param parentKey - The parent key used for concatenation
 * @param separator - The character used to separate nested keys
 * @returns A flattened object where nested keys are concatenated with the separator
 * @throws {Error} If the input is not a plain object or contains non-JSONable values
 *
 * @example
 * const input = {
 *   user: {
 *     name: 'John',
 *     address: {
 *       city: 'Boston'
 *     }
 *   }
 * };
 *
 * const flat = flattenObject(input);
 * // {
 * //   user_name: 'John',
 * //   user_address_city: 'Boston'
 * // }
 */
export const flattenObject = (
    obj: Record<string, unknown>,
    parentKey: string = '',
    separator: string = '_'
): Record<string, unknown> => {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
        throw new Error('Input must be a plain object');
    }

    if (!isPlainObject(obj)) {
        throw new Error(
            'Input must be a plain object, not a class instance or special object type'
        );
    }

    if (!isJSONable(obj)) {
        throw new Error('Input object must be JSON-serializable');
    }

    return Object.keys(obj).reduce((result, key) => {
        const newKey = parentKey ? `${parentKey}${separator}${key}` : key;
        const value = obj[key];

        if (value && typeof value === 'object' && isPlainObject(value)) {
            if (!isJSONable(value)) {
                throw new Error(
                    `Property "${newKey}" contains non-JSON-serializable data`
                );
            }
            // Recursively flatten nested objects
            const flattened = flattenObject(
                value as Record<string, unknown>,
                newKey,
                separator
            );
            return { ...result, ...flattened };
        }

        // For non-object values, add them directly
        return { ...result, [newKey]: value };
    }, {} as Record<string, unknown>);
};
